import { useEffect, useRef } from 'react';

import type { SectorGroup } from '../../types';
import { accentuate } from '../../lib/accents';
import { IconCheck, IconChevron, IconClose } from '../components/Icons';

interface SectorSheetProps {
  groups: readonly SectorGroup[];
  /** Groupe actuellement retenu, ou `null` pour « toutes catégories ». */
  currentGroupId: string | null;
  /**
   * Rend l'identifiant du **premier métier** du groupe choisi, ou une chaîne
   * vide pour « toutes catégories ».
   *
   * Le groupe seul n'existe pas côté API : une recherche vise un métier précis
   * (`sectorId`) ou aucun. Choisir « Restauration » revient donc à viser
   * « Restaurant », que l'écran de recherche laisse ensuite affiner.
   */
  onPick: (sectorId: string) => void;
  onClose: () => void;
}

/**
 * Le choix du secteur, sur un écran entier.
 *
 * Un menu déroulant de six groupes tenant chacun quatre métiers oblige à
 * viser deux fois dans une liste système minuscule. Ici chaque groupe est une
 * cible de 60 px de haut, avec le nombre de métiers qu'il couvre — on choisit
 * en un geste, sans lire.
 *
 * Au bureau la même chose devient une carte centrée sur un voile : voir
 * `app.css`, la bascule est entièrement dans la feuille de style.
 */
export function SectorSheet({ groups, currentGroupId, onPick, onClose }: SectorSheetProps) {
  const card = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement;
    card.current?.focus({ preventScroll: true });
    return () => {
      if (previous instanceof HTMLElement) previous.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="sector-sheet" role="dialog" aria-modal="true" aria-label="Choisir un secteur">
      <button type="button" className="sector-sheet-veil" aria-label="Fermer" onClick={onClose} />

      <div className="sector-sheet-card" ref={card} tabIndex={-1}>
        <div className="sector-sheet-head">
          <h2 className="title title-lg">Choisir un secteur</h2>
          <button
            type="button"
            className="btn-round btn-round-line"
            onClick={onClose}
            aria-label="Fermer"
          >
            <IconClose size={16} />
          </button>
        </div>

        <p className="sector-sheet-lead">
          Un seul secteur à la fois, puis vous affinez le métier sur l’écran de recherche.
        </p>

        <div className="sector-sheet-list">
          <Option
            label="Toutes catégories"
            detail="Le tour complet du rayon, tous métiers confondus"
            selected={currentGroupId === null}
            onPick={() => {
              onPick('');
              onClose();
            }}
          />

          {groups.map((group) => (
            <Option
              key={group.id}
              label={accentuate(group.label)}
              detail={`${group.sectors.length} ${group.sectors.length > 1 ? 'métiers' : 'métier'} — ${group.sectors
                .slice(0, 3)
                .map((sector) => accentuate(sector.label))
                .join(', ')}${group.sectors.length > 3 ? '…' : ''}`}
              selected={currentGroupId === group.id}
              onPick={() => {
                const first = group.sectors[0];
                if (first !== undefined) onPick(first.id);
                onClose();
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Option({
  label,
  detail,
  selected,
  onPick,
}: {
  label: string;
  detail: string;
  selected: boolean;
  onPick: () => void;
}) {
  return (
    <button type="button" className="sector-option" aria-pressed={selected} onClick={onPick}>
      <span className="sector-body">
        <span className="sector-option-label">{label}</span>
        <span className="sector-option-detail">{detail}</span>
      </span>
      {selected ? (
        <IconCheck className="sector-option-mark" size={20} />
      ) : (
        <IconChevron className="sector-option-mark" size={15} />
      )}
    </button>
  );
}
